// Versão: 1.05
// Data..: 2026-10-14
// Conta do usuário: dados do perfil e troca de senha.
// v1.05: NotificationSection mostra o estado da permissão do navegador (granted/default/
// denied/unsupported) com aviso inline em cor de alerta e botão "Testar notificação do
// sistema"; reavalia a permissão ao focar a aba.
// v1.04: NotificationSection ganha controle de volume do alerta (slider 0..1) e prévia
// dos sons respeita o volume atual.
// v1.03: Ponto Eletrônico — TimeclockConfigSection (horários HH:mm de entrada/almoço/saída
// e toggles de alerta por e-mail/Discord em cada momento; só staff).
// v1.01: ZimbraCalendarSection (integração CalDAV ↔ Zimbra do menu Tarefas; só staff).
// v1.02: testConnection exibe mensagem pt-BR específica (URL/auth/404/rede) via toast.

import { useState, useEffect, type FormEvent } from 'react';
import { UserRole, type CaldavConfigView, type TimeclockConfig, type TimeclockConfigInput } from '@makweb/shared';
import { api } from '../lib/api.js';
import { Modal } from '../components/Modal.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { useDialog } from '../context/DialogContext.js';
import { PageHeader } from '../components/PageHeader.js';
import { SOUND_OPTIONS, playSound, type SoundId } from '../lib/sounds.js';
import { loadPrefs, savePrefs, type NotificationPrefs } from '../lib/notificationPrefs.js';
import { getMfaEnabled, startTotpSetup, verifyTotp, disableTotp, totpUri } from '../lib/auth.js';

const roleLabel: Record<string, string> = { admin: 'Administrador', agent: 'Atendente', client: 'Cliente' };

export function AccountPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (pwd.length < 10) return toast.error('A senha deve ter ao menos 10 caracteres');
    if (pwd !== confirm) return toast.error('As senhas não coincidem');
    setSaving(true);
    try {
      await api.post('/me/password', { password: pwd });
      toast.success('Senha alterada com sucesso');
      setPwd(''); setConfirm('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao alterar senha');
    } finally {
      setSaving(false);
    }
  };

  const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.AGENT;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <PageHeader title="Minha conta" description="Seus dados, alertas e senha." />

      <div className="card">
        <h3>Perfil</h3>
        <div className="flex items-center gap-4">
          <span
            style={{
              width: 56, height: 56, borderRadius: '50%', background: 'var(--brand)',
              display: 'grid', placeItems: 'center', color: '#fff', fontSize: 24, fontWeight: 600,
            }}
          >
            {(user?.name ?? '?').charAt(0).toUpperCase()}
          </span>
          <div>
            <div className="font-semibold" style={{ fontSize: 16 }}>{user?.name}</div>
            <div className="text-muted text-sm">{user?.email}</div>
            <span className="badge badge-normal mt-2">{roleLabel[user?.role ?? ''] ?? user?.role}</span>
          </div>
        </div>
      </div>

      <MfaSection email={user?.email ?? ''} />

      <NotificationSection />

      {isStaff && <DiscordSection />}

      {isStaff && <TimeclockConfigSection />}

      {isStaff && <ZimbraCalendarSection email={user?.email ?? ''} />}

      <form onSubmit={changePassword} className="card mt-4">
        <h3>Alterar senha</h3>
        <div className="field">
          <label className="label">Nova senha</label>
          <input className="input" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required />
          <div className="text-dim text-xs mt-2">Mínimo 10 caracteres, com maiúscula, minúscula e número.</div>
        </div>
        <div className="field">
          <label className="label">Confirmar nova senha</label>
          <input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </div>
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? 'Salvando...' : 'Alterar senha'}
        </button>
      </form>

      {user?.role === UserRole.CLIENT && (
        <div className="card mt-4 text-muted text-sm">
          Você está acessando como cliente. Para dúvidas sobre sua conta, abra um chamado no departamento de Suporte.
        </div>
      )}
    </div>
  );
}

// Verificação em duas etapas (MFA por app autenticador / TOTP).
function MfaSection({ email }: { email: string }) {
  const toast = useToast();
  const { confirm: askConfirm } = useDialog();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getMfaEnabled().then(setEnabled).catch(() => setEnabled(false));
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const s = await startTotpSetup();
      setSecret(s);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao iniciar configuração');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await verifyTotp(code.trim());
      toast.success('Verificação em duas etapas ativada');
      setSecret(''); setCode(''); setEnabled(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Código inválido');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    const ok = await askConfirm({
      title: 'Desativar MFA',
      message: 'Desativar a verificação em duas etapas?',
      confirmLabel: 'Desativar', danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await disableTotp();
      toast.success('Verificação em duas etapas desativada');
      setEnabled(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao desativar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mt-4">
      <h3>🔐 Verificação em duas etapas (MFA)</h3>
      <p className="text-muted text-sm">
        Proteja o acesso exigindo um código de um app autenticador (Google Authenticator, Authy, etc.).
      </p>

      {enabled === null ? (
        <div className="flex justify-center" style={{ padding: 16 }}><span className="spinner" /></div>
      ) : enabled ? (
        <div className="flex items-center justify-between mt-2">
          <span className="badge badge-resolved">Ativado</span>
          <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={disable} disabled={busy}>Desativar</button>
        </div>
      ) : secret ? (
        <div className="mt-2">
          <p className="text-sm">1. Adicione esta chave no seu app autenticador (ou use o link):</p>
          <div className="field">
            <label className="label">Chave secreta</label>
            <input className="input" readOnly value={secret} style={{ fontFamily: 'monospace', letterSpacing: '0.1em' }} onFocus={(e) => e.target.select()} />
          </div>
          <div className="text-xs mb-3">
            <a href={totpUri(secret, email)} className="text-sm">Abrir no app autenticador ↗</a>
          </div>
          <p className="text-sm">2. Digite o código de 6 dígitos gerado pelo app:</p>
          <div className="flex gap-2">
            <input
              className="input"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              style={{ letterSpacing: '0.2em', textAlign: 'center', maxWidth: 160 }}
            />
            <button className="btn btn-primary" onClick={confirm} disabled={busy || code.length < 6}>Ativar</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-primary mt-2" onClick={startSetup} disabled={busy}>
          {busy ? 'Aguarde...' : 'Ativar verificação em duas etapas'}
        </button>
      )}
    </div>
  );
}

// Preferências de notificação do portal (som, som escolhido, notificação do sistema).
// Salvas localmente no navegador (por dispositivo).
// Texto de desbloqueio quando a permissão está 'denied' (reusado no inline e nos toasts).
const DESKTOP_BLOCKED_MSG =
  'As notificações estão BLOQUEADAS neste navegador. Para ativar: clique no cadeado ao lado do endereço → Notificações → Permitir, e recarregue a página.';

// Lê o estado atual da permissão de notificação do navegador (ou 'unsupported').
function readPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

function NotificationSection() {
  const toast = useToast();
  const [prefs, setPrefs] = useState<NotificationPrefs>(() => loadPrefs());
  // Estado reativo da permissão. Notification.permission não emite evento de mudança,
  // então reavaliamos ao focar a aba (focus/visibilitychange) e após cada ação.
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => readPermission());

  const refreshPermission = () => setPermission(readPermission());

  // Reavalia a permissão quando a aba volta ao foco (o usuário pode alterá-la no cadeado
  // do navegador sem recarregar a página).
  useEffect(() => {
    const onFocus = () => refreshPermission();
    const onVisibility = () => { if (document.visibilityState === 'visible') refreshPermission(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const update = (patch: Partial<NotificationPrefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    savePrefs(next);
  };

  const enableDesktop = () => {
    if (!('Notification' in window)) return toast.error('Navegador sem suporte a notificações.');
    if (Notification.permission === 'granted') { update({ desktopEnabled: true }); return refreshPermission(); }
    Notification.requestPermission().then((p) => {
      if (p === 'granted') { update({ desktopEnabled: true }); toast.success('Notificações do sistema ativadas'); }
      else toast.error('Permissão negada pelo navegador.');
      refreshPermission();
    }).catch(() => refreshPermission());
  };

  // Dispara uma notificação de teste imediata (permissão já concedida).
  const fireTestNotification = () => {
    try {
      new Notification('Makweb Portal', { body: 'Notificações do sistema ativadas com sucesso.' });
    } catch (err) {
      console.warn('Falha ao exibir notificação de teste', err);
      toast.error('Não foi possível exibir a notificação de teste.');
    }
    refreshPermission();
  };

  // Botão "Testar notificação do sistema": pede permissão quando pendente e dispara.
  const testDesktop = () => {
    if (!('Notification' in window)) return toast.error('Navegador sem suporte a notificações.');
    const current = Notification.permission;
    if (current === 'granted') return fireTestNotification();
    if (current === 'denied') { toast.error(DESKTOP_BLOCKED_MSG); return refreshPermission(); }
    // 'default': solicita a permissão e, se concedida, dispara o teste.
    Notification.requestPermission().then((p) => {
      refreshPermission();
      if (p === 'granted') fireTestNotification();
      else if (p === 'denied') toast.error(DESKTOP_BLOCKED_MSG);
    }).catch(() => refreshPermission());
  };

  return (
    <div className="card mt-4">
      <h3>🔔 Alertas do portal</h3>
      <p className="text-muted text-sm">
        Avisos de novos chamados, mensagens de WhatsApp e tarefas atribuídas a você.
        Estas preferências valem apenas neste navegador.
      </p>

      <label className="flex items-center gap-2 mt-2" style={{ cursor: 'pointer' }}>
        <input type="checkbox" checked={prefs.soundEnabled} onChange={(e) => update({ soundEnabled: e.target.checked })} />
        <span>Tocar som ao receber um alerta</span>
      </label>

      <div className="field mt-2">
        <label className="label">Som do alerta</label>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          {SOUND_OPTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`btn btn-sm ${prefs.sound === s.id ? 'btn-primary' : 'btn-ghost'}`}
              disabled={!prefs.soundEnabled}
              onClick={() => { update({ sound: s.id as SoundId }); playSound(s.id, prefs.volume); }}
            >
              🔊 {s.label}
            </button>
          ))}
        </div>
        <div className="text-dim text-xs mt-2">Clique em um som para selecioná-lo e ouvir uma prévia.</div>
      </div>

      <div className="field mt-2">
        <label className="label">Volume do alerta</label>
        {/* Slider 0..1; ao ajustar, toca uma prévia com o volume escolhido. */}
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={prefs.volume}
          disabled={!prefs.soundEnabled}
          onChange={(e) => { const v = Number(e.target.value); update({ volume: v }); playSound(prefs.sound, v); }}
          style={{ width: '100%' }}
        />
        <div className="text-dim text-xs mt-2">{Math.round(prefs.volume * 100)}% — arraste para ajustar e ouvir uma prévia.</div>
      </div>

      <label className="flex items-center gap-2 mt-2" style={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={prefs.desktopEnabled}
          onChange={(e) => (e.target.checked ? enableDesktop() : update({ desktopEnabled: false }))}
        />
        <span>Mostrar notificação do sistema (desktop)</span>
      </label>

      {/* Indicador do estado da permissão (inline, sem modal). Quando o checkbox está
          marcado mas a permissão não foi concedida, destacamos em cor de alerta. */}
      {(() => {
        const enabledButNotGranted = prefs.desktopEnabled && permission !== 'granted' && permission !== 'unsupported';
        if (permission === 'granted') {
          return (
            <div className="text-sm mt-2" style={{ color: 'var(--success)' }}>
              ✓ Permissão concedida — as notificações do sistema estão ativas.
            </div>
          );
        }
        if (permission === 'denied') {
          return (
            <div className="card mt-2" style={{ borderLeft: '3px solid var(--danger)' }}>
              <strong>⚠ {DESKTOP_BLOCKED_MSG}</strong>
            </div>
          );
        }
        if (permission === 'unsupported') {
          return (
            <div className="card mt-2" style={{ borderLeft: '3px solid var(--warning)' }}>
              <strong>⚠ Este navegador não suporta notificações do sistema.</strong>
            </div>
          );
        }
        // 'default': permissão pendente. Em alerta se o checkbox já está marcado.
        return (
          <div
            className={enabledButNotGranted ? 'card mt-2' : 'text-sm mt-2'}
            style={
              enabledButNotGranted
                ? { borderLeft: '3px solid var(--warning)' }
                : { color: 'var(--warning)' }
            }
          >
            {enabledButNotGranted ? (
              <strong>⚠ Permissão pendente — clique em "Testar notificação do sistema" e aceite o pedido do navegador. O item marcado NÃO garante que as notificações aparecerão até você aceitar.</strong>
            ) : (
              'Permissão pendente — clique em "Testar notificação do sistema" e aceite o pedido do navegador.'
            )}
          </div>
        );
      })()}

      <div className="mt-2">
        <button type="button" className="btn btn-sm btn-ghost" onClick={testDesktop}>
          Testar notificação do sistema
        </button>
      </div>
    </div>
  );
}

// Configuração do webhook pessoal do Discord (alertas direcionados ao usuário).
function DiscordSection() {
  const toast = useToast();
  const [webhook, setWebhook] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ discordWebhook?: string }>('/me').then((u) => setWebhook(u.discordWebhook ?? '')).catch(() => {});
  }, []);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch('/me', { discordWebhook: webhook });
      toast.success('Webhook do Discord salvo');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="card mt-4">
      <h3>🔔 Meus alertas no Discord</h3>
      <p className="text-muted text-sm">
        Cole aqui o webhook do seu canal no Discord para receber os alertas dos chamados
        atribuídos a você. Se vazio, usamos o canal geral.
      </p>
      <div className="field">
        <label className="label">Webhook pessoal do Discord</label>
        <input className="input" type="password" value={webhook} onChange={(e) => setWebhook(e.target.value)} placeholder="https://discord.com/api/webhooks/..." />
      </div>
      <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar webhook'}</button>
    </form>
  );
}

// Momentos da jornada e seus rótulos (ordem de exibição).
const TIMECLOCK_MOMENTS: { key: 'start' | 'lunchOut' | 'lunchIn' | 'end'; label: string; field: 'startAt' | 'lunchOutAt' | 'lunchInAt' | 'endAt' }[] = [
  { key: 'start', label: 'Entrada', field: 'startAt' },
  { key: 'lunchOut', label: 'Saída para o almoço', field: 'lunchOutAt' },
  { key: 'lunchIn', label: 'Volta do almoço', field: 'lunchInAt' },
  { key: 'end', label: 'Saída', field: 'endAt' },
];

// Configuração do horário de trabalho e dos alertas da jornada (Ponto Eletrônico). Só
// staff (ADMIN/AGENT), pois são os papéis que batem ponto. Os horários são em HH:mm no
// fuso America/Sao_Paulo; cada momento pode disparar alerta por e-mail e/ou Discord.
function TimeclockConfigSection() {
  const toast = useToast();
  const emptyAlerts = {
    start: { email: false, discord: false },
    lunchOut: { email: false, discord: false },
    lunchIn: { email: false, discord: false },
    end: { email: false, discord: false },
  };
  const [form, setForm] = useState<TimeclockConfigInput>({
    startAt: '', lunchOutAt: '', lunchInAt: '', endAt: '', alerts: emptyAlerts,
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ config: TimeclockConfig | null }>('/me/timeclock/config')
      .then((r) => {
        if (r.config) {
          setForm({
            startAt: r.config.startAt ?? '',
            lunchOutAt: r.config.lunchOutAt ?? '',
            lunchInAt: r.config.lunchInAt ?? '',
            endAt: r.config.endAt ?? '',
            alerts: r.config.alerts ?? emptyAlerts,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verdadeiro quando nenhum horário foi definido ainda (mostra o banner de aviso).
  const notConfigured = loaded && !form.startAt && !form.lunchOutAt && !form.lunchInAt && !form.endAt;

  const setTime = (field: 'startAt' | 'lunchOutAt' | 'lunchInAt' | 'endAt', value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toggleAlert = (moment: 'start' | 'lunchOut' | 'lunchIn' | 'end', channel: 'email' | 'discord', value: boolean) =>
    setForm((f) => ({ ...f, alerts: { ...f.alerts, [moment]: { ...f.alerts[moment], [channel]: value } } }));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Envia apenas os horários preenchidos (vazio = não configurado).
      const body: TimeclockConfigInput = {
        startAt: form.startAt || undefined,
        lunchOutAt: form.lunchOutAt || undefined,
        lunchInAt: form.lunchInAt || undefined,
        endAt: form.endAt || undefined,
        alerts: form.alerts,
      };
      await api.put('/me/timeclock/config', body);
      toast.success('Horário do ponto salvo');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar o horário');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="card mt-4">
      <h3>🕒 Ponto — meu horário</h3>
      <p className="text-muted text-sm">
        Defina os horários da sua jornada e escolha em quais momentos quer receber alertas
        (por e-mail e/ou Discord). Os horários usam o fuso de Brasília (HH:mm).
      </p>

      {notConfigured && (
        <div className="card mb-3" style={{ borderLeft: '3px solid var(--warning)' }}>
          <strong>⚠ Horário não configurado.</strong>
          <div className="text-dim text-sm mt-1">Defina ao menos a entrada e a saída para habilitar os alertas da jornada.</div>
        </div>
      )}

      <table className="table" style={{ marginTop: 8 }}>
        <thead><tr><th>Momento</th><th>Horário</th><th>Alerta por e-mail</th><th>Alerta no Discord</th></tr></thead>
        <tbody>
          {TIMECLOCK_MOMENTS.map((m) => (
            <tr key={m.key}>
              <td className="font-semibold text-sm">{m.label}</td>
              <td>
                <input
                  className="input"
                  type="time"
                  value={form[m.field] ?? ''}
                  onChange={(e) => setTime(m.field, e.target.value)}
                  style={{ maxWidth: 130 }}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={form.alerts[m.key].email}
                  onChange={(e) => toggleAlert(m.key, 'email', e.target.checked)}
                  aria-label={`Alerta por e-mail — ${m.label}`}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={form.alerts[m.key].discord}
                  onChange={(e) => toggleAlert(m.key, 'discord', e.target.checked)}
                  aria-label={`Alerta no Discord — ${m.label}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button className="btn btn-primary mt-3" type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar horário'}</button>
    </form>
  );
}

// Integração do menu Tarefas com o calendário Zimbra (CalDAV, bidirecional). Só staff.
// A senha de app NUNCA é pré-preenchida; o backend só informa se já há uma configurada.
function ZimbraCalendarSection({ email }: { email: string }) {
  const toast = useToast();
  const [enabled, setEnabled] = useState(false);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordSet, setPasswordSet] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Sugestão de URL CalDAV do Zimbra montada a partir do e-mail do usuário.
  const suggestedUrl = email ? `https://makweb.email.com.br/dav/${email}/Calendar` : '';

  useEffect(() => {
    api
      .get<CaldavConfigView>('/me/caldav')
      .then((cfg) => {
        setEnabled(cfg.enabled);
        setCalendarUrl(cfg.calendarUrl || suggestedUrl);
        setUsername(cfg.username || email);
        setPasswordSet(cfg.passwordSet);
        setLastSyncAt(cfg.lastSyncAt);
      })
      .catch(() => {
        // Sem config ainda: pré-preenche a sugestão.
        setCalendarUrl(suggestedUrl);
        setUsername(email);
      });
  }, [email, suggestedUrl]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Envia a senha só quando o usuário digitou algo (evita apagar a existente).
      const body: Record<string, unknown> = { enabled, calendarUrl, username };
      if (password) body.password = password;
      const updated = await api.put<CaldavConfigView>('/me/caldav', body);
      setPasswordSet(updated.passwordSet);
      setPassword('');
      toast.success('Configuração do calendário salva');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      // O backend devolve mensagem pt-BR específica (URL/auth/404/rede). Exibimos via
      // toast existente; o status é só diagnóstico adicional.
      const res = await api.post<{ ok: boolean; message?: string; status?: number }>('/me/caldav/test');
      if (res.ok) toast.success(res.message || 'Conexão bem-sucedida');
      else toast.error(res.message || 'Falha na conexão');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao testar conexão');
    } finally {
      setTesting(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.post<{ pulled: number; message?: string }>('/me/caldav/sync');
      toast.success(res.message || 'Sincronização concluída');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <form onSubmit={save} className="card mt-4">
      <h3>📅 Calendário Zimbra (Tarefas)</h3>
      <p className="text-muted text-sm">
        Sincronize suas tarefas com o calendário do Zimbra (CalDAV). As tarefas atribuídas a
        você aparecem no calendário, e eventos criados lá viram tarefas no portal.{' '}
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setHelpOpen(true)}>
          Como obter a senha de app?
        </button>
      </p>

      <label className="flex items-center gap-2 mt-2" style={{ cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Habilitar sincronização</span>
      </label>

      <div className="field mt-2">
        <label className="label">URL do calendário (CalDAV)</label>
        <input
          className="input"
          type="url"
          value={calendarUrl}
          onChange={(e) => setCalendarUrl(e.target.value)}
          placeholder={suggestedUrl || 'https://makweb.email.com.br/dav/seu-email/Calendar'}
        />
      </div>

      <div className="field">
        <label className="label">Usuário</label>
        <input
          className="input"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={email}
        />
      </div>

      <div className="field">
        <label className="label">Senha de app {passwordSet && <span className="badge badge-resolved">configurada</span>}</label>
        <input
          className="input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={passwordSet ? '•••••••• (deixe em branco para manter)' : 'senha de app do Zimbra'}
          autoComplete="new-password"
        />
      </div>

      {lastSyncAt && (
        <div className="text-dim text-xs mb-3">
          Última sincronização: {new Date(lastSyncAt).toLocaleString('pt-BR')}
        </div>
      )}

      <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
        <button className="btn btn-ghost" type="button" onClick={testConnection} disabled={testing}>
          {testing ? 'Testando...' : 'Testar conexão'}
        </button>
        <button className="btn btn-ghost" type="button" onClick={syncNow} disabled={syncing}>
          {syncing ? 'Sincronizando...' : 'Sincronizar agora'}
        </button>
      </div>

      <Modal
        open={helpOpen}
        title="Senha de app do Zimbra"
        onClose={() => setHelpOpen(false)}
        footer={<button className="btn btn-primary" type="button" onClick={() => setHelpOpen(false)}>Entendi</button>}
      >
        <p className="text-sm">
          Para a sincronização funcionar, use uma senha de app (ou a senha da conta) do seu
          e-mail Zimbra. A senha é guardada de forma segura e nunca é exibida de volta.
        </p>
        <p className="text-sm mt-2">
          A URL do calendário segue o padrão{' '}
          <code>https://makweb.email.com.br/dav/&lt;seu-email&gt;/Calendar</code>. Se o seu
          servidor usa outro endereço, ajuste o campo URL.
        </p>
      </Modal>
    </form>
  );
}
